import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users,
  Building2,
  UserCircle2,
  FileText,
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  Edit,
  Trash2,
  LayoutGrid,
  List,
  AlertTriangle,
  AlertCircle,
  CreditCard,
  X,
  Bell,
  Printer,
  Download,
  FileBarChart,
  Sparkles,
  ShieldAlert,
} from "lucide-react";
import SearchFilterBar from "@/components/SearchFilterBar";
import { usePagination } from "@/lib/usePagination";
import { useFilters } from "@/hooks/useFilters";
import { formatPhoneNumber } from "@/utils/helpers";
import BillingPlanSection from "@/components/BillingPlanSection";
import { Client360Dialog } from "@/components/dialogs/Client360Dialog";
import AnagraficaConsistencyDialog from "@/components/dialogs/AnagraficaConsistencyDialog";
import SuiteSubnav from "@/components/suite/SuiteSubnav";

/**
 * Get document expiry status for a client
 */
const getDocumentExpiryStatus = (client) => {
  if (!client.documentExpiryDate) return null;
  
  const today = new Date();
  const parts = client.documentExpiryDate.split('/');
  if (parts.length !== 3) return null;
  
  const expiryDate = new Date(parts[2], parts[1] - 1, parts[0]);
  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiry < 0) {
    return { status: 'expired', days: Math.abs(daysUntilExpiry), message: `Scaduto da ${Math.abs(daysUntilExpiry)} giorni` };
  } else if (daysUntilExpiry <= 60) {
    return { status: 'warning', days: daysUntilExpiry, message: `Scade tra ${daysUntilExpiry} giorni` };
  }
  return null;
};

/**
 * Clienti Tab Component
 * Shows client list with search, filtering, and CRUD operations
 * Supports both card view and compact row view
 */
function ClientiTab({
  clients,
  cases,
  handleEditClient,
  handleDeleteClient,
  onNewClient,
  onOpenClient360,
  // Apre il Registro Attività pre-filtrato per il cliente (entry-point
  // B della P1 — deep-link verso `RegistroAttivitaTab`).
  onOpenRegistroAttivita,
  // Callback opzionale per ricaricare la lista clienti dopo un autofix
  // dell'anagrafica (v1.4.1 sweep CF↔Comune).
  onReloadClients,
}) {
  // View mode state (card or list)
  const [viewMode, setViewMode] = useState('list'); // 'card' or 'list'
  const [expandedClientId, setExpandedClientId] = useState(null);

  // Cliente 360° quick-access from the client list (17/07/2026 — v1.4.1).
  // Cliccando la colonna "Pratiche" (o l'icona Sparkles nella card) si
  // apre direttamente la timeline aggregata SENZA passare da Modifica.
  const [client360Client, setClient360Client] = useState(null);
  const openClient360 = (client) => setClient360Client(client);
  const closeClient360 = () => setClient360Client(null);

  // Sweep Anagrafica CF↔Comune (17/07/2026 · v1.4.1 sera).
  const [consistencyOpen, setConsistencyOpen] = useState(false);
  
  // Alert popup state
  const [showAlertPopup, setShowAlertPopup] = useState(false);
  const [alertsShownOnce, setAlertsShownOnce] = useState(false);
  
  // Check for document expiry alerts on mount
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  
  useEffect(() => {
    const alerts = clients
      .map(client => {
        const status = getDocumentExpiryStatus(client);
        if (status) {
          return { client, ...status };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.status === 'expired' && b.status !== 'expired') return -1;
        if (b.status === 'expired' && a.status !== 'expired') return 1;
        return a.days - b.days;
      });
    
    setExpiryAlerts(alerts);
    
    // Show popup automatically if there are critical alerts (expired docs)
    // Only show once per session
    if (!alertsShownOnce && alerts.length > 0) {
      const hasExpired = alerts.some(a => a.status === 'expired');
      if (hasExpired) {
        setShowAlertPopup(true);
        setAlertsShownOnce(true);
      }
    }
  }, [clients, alertsShownOnce]);
  
  // Filtering configuration - NO default management filter (clients are global)
  const {
    searchQuery,
    setSearchQuery,
    filters,
    setFilter,
    resetFilters,
    hasActiveFilters,
    filteredData: filteredClientsUnsorted,
    totalCount,
    filteredCount
  } = useFilters(clients, {
    searchFields: ['fullName', 'taxCode', 'email', 'phone', 'street', 'city', 'notes'],
    filterFields: { clientType: 'clientType', managementType: 'managementType' },
    defaultFilters: {}
  });
  
  // Sort clients alphabetically by name
  const filteredClients = useMemo(() => {
    return [...filteredClientsUnsorted].sort((a, b) => 
      (a.fullName || '').localeCompare(b.fullName || '', 'it', { sensitivity: 'base' })
    );
  }, [filteredClientsUnsorted]);

  // Paginazione condivisa (20/02/2026).
  const {
    paged: pagedClients,
    paginationBar: clientsPaginationBar,
  } = usePagination(filteredClients, {
    storageKey: "anagrafica-clienti",
    itemLabelSingular: "cliente",
    itemLabelPlural: "clienti",
  });

  // Filter options
  const filterConfig = [
    {
      key: 'managementType',
      label: 'Gestione',
      allLabel: 'Tutte le gestioni',
      options: [
        { value: 'studio', label: 'Gestione Studio' },
        { value: 'agenzia', label: 'Gestione Agenzia' }
      ]
    },
    {
      key: 'clientType',
      label: 'Tipo Cliente',
      allLabel: 'Tutti i tipi',
      options: [
        { value: 'Privato', label: 'Privato' },
        { value: 'Azienda', label: 'Azienda' },
        { value: 'Professionista', label: 'Professionista' },
        { value: 'Ditta individuale', label: 'Ditta individuale' }
      ]
    }
  ];

  // Get client case counts
  const getClientCases = (clientId) => {
    const clientCases = cases.filter((c) => c.clientId === clientId);
    const activeCases = clientCases.filter(
      (c) => c.status !== "Conclusa"
    ).length;
    return { total: clientCases.length, active: activeCases };
  };

  // Format address for display
  const formatAddress = (client) => {
    const parts = [];
    if (client.street) parts.push(client.street);
    if (client.postalCode || client.city) {
      const cityPart = [client.postalCode, client.city].filter(Boolean).join(' ');
      parts.push(cityPart);
    }
    if (client.province) parts.push(`(${client.province})`);
    return parts.join(', ') || client.address || '—';
  };

  // Print client list
  const handlePrintClients = () => {
    const rows = filteredClientsUnsorted.map(c => 
      `<tr><td>${c.fullName || ''}</td><td>${c.taxCode || ''}</td><td>${c.vatNumber || ''}</td><td>${c.email || ''}</td><td>${c.phone || ''}</td><td>${formatAddress(c)}</td></tr>`
    ).join('');
    const html = `<html><head><title>Anagrafica Clienti</title><style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px;text-align:left;font-size:11px}th{background:#f3f4f6;font-weight:bold}</style></head><body><h2>Anagrafica Clienti (${filteredClientsUnsorted.length})</h2><table><tr><th>Cliente</th><th>C.F.</th><th>P.IVA</th><th>Email</th><th>Telefono</th><th>Indirizzo</th></tr>${rows}</table></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  };

  // Export client list to Excel CSV
  const handleExportClientsExcel = () => {
    const headers = ['Cliente', 'Tipo', 'C.F.', 'P.IVA', 'Email', 'Telefono', 'Indirizzo', 'Gestione'];
    const rows = filteredClientsUnsorted.map(c => [
      c.fullName || '', c.clientType || '', c.taxCode || '', c.vatNumber || '',
      c.email || '', c.phone || '', formatAddress(c), c.managementType === 'agenzia' ? 'Agenzia' : 'Studio'
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `anagrafica_clienti_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  return (
    <div className="space-y-2" data-testid="clienti-module">
      {/* Header compact — v1.8.0 UX Operativa v2: titolo + azione
          principale su una sola riga, sottotitolo inline, recuperati
          ~35px verticali rispetto al layout precedente. */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-1 py-1" data-testid="clienti-module-header">
        <div className="flex items-center gap-3">
          <span
            className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "var(--gis-green-50)" }}
          >
            <Users className="h-4 w-4" style={{ color: "var(--gis-green)" }} />
          </span>
          <div>
            <h2 className="gis-heading font-bold text-slate-900 text-lg leading-tight">Anagrafica Clienti</h2>
            <p className="text-[11px] text-slate-500 gis-body">Registro completo · visibile a tutti gli operatori</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConsistencyOpen(true)}
          className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="open-consistency-report"
          title="Controllo qualità: verifica coerenza CF ↔ Comune di Nascita su tutta l'anagrafica"
        >
          <ShieldAlert className="mr-1.5 h-4 w-4" /> Verifica anagrafica
        </Button>
      </div>

      <SuiteSubnav
        items={[
          { value: "elenco", label: "Elenco clienti" },
          { value: "nuovo", label: "Nuovo cliente" },
          { value: "c360", label: "Cliente 360°" },
        ]}
        activeValue="elenco"
        onSelect={(value) => {
          if (value === "nuovo") onNewClient?.();
          if (value === "c360") onOpenClient360?.();
        }}
        className="mb-2"
        testid="suite-subnav-clients"
      />

      {/* Document Expiry Alerts */}
      {expiryAlerts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-orange-800">
              <AlertTriangle className="h-4 w-4" />
              Documenti in Scadenza ({expiryAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="flex flex-wrap gap-2">
              {expiryAlerts.slice(0, 5).map((alert, idx) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className={`cursor-pointer ${
                    alert.status === 'expired'
                      ? 'border-red-400 bg-red-100 text-red-800'
                      : 'border-orange-400 bg-orange-100 text-orange-800'
                  }`}
                  onClick={() => handleEditClient(alert.client)}
                >
                  {alert.status === 'expired' ? (
                    <AlertCircle className="h-3 w-3 mr-1" />
                  ) : (
                    <AlertTriangle className="h-3 w-3 mr-1" />
                  )}
                  {alert.client.fullName}: {alert.message}
                </Badge>
              ))}
              {expiryAlerts.length > 5 && (
                <Badge variant="outline" className="border-slate-300">
                  +{expiryAlerts.length - 5} altri
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filter Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <SearchFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Cerca per nome, codice fiscale, email, telefono, città..."
            filters={filterConfig}
            filterValues={filters}
            onFilterChange={setFilter}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
            totalCount={totalCount}
            filteredCount={filteredCount}
          />
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="h-8 px-3"
            data-testid="view-list-btn"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'card' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('card')}
            className="h-8 px-3"
            data-testid="view-card-btn"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Client List */}
      <Card className="shadow-md">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>{filteredCount} {filteredCount === totalCount ? "clienti" : `di ${totalCount} clienti`}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-xs px-3" onClick={handlePrintClients} data-testid="print-clients-button" title={`Stampa ${filteredCount} righe filtrate`}>
                <Printer className="h-3 w-3 mr-1" /> Stampa
                <span className="ml-1 opacity-70">({filteredCount})</span>
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs px-3" onClick={handleExportClientsExcel} data-testid="export-clients-excel-button" title={`Esporta ${filteredCount} righe filtrate`}>
                <Download className="h-3 w-3 mr-1" /> Esporta Excel
                <span className="ml-1 opacity-70">({filteredCount})</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {viewMode === 'list' ? (
            /* Compact Row View */
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[200px]">Cliente</TableHead>
                    <TableHead>C.F. / P.IVA</TableHead>
                    <TableHead>Contatti</TableHead>
                    <TableHead>Indirizzo</TableHead>
                    <TableHead className="text-center w-[80px]">Pratiche</TableHead>
                    <TableHead className="w-[100px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedClients.map((client) => {
                    const caseCounts = getClientCases(client.id);
                    const docStatus = getDocumentExpiryStatus(client);
                    
                    return (
                      <React.Fragment key={client.id}>
                      <TableRow
                        className="group hover:bg-slate-50"
                        data-testid={`client-row-${client.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                              client.clientType === "Azienda" || client.clientType === "Ditta individuale"
                                ? 'bg-blue-100'
                                : 'bg-green-100'
                            }`}>
                              {client.clientType === "Azienda" || client.clientType === "Ditta individuale" ? (
                                <Building2 className="h-4 w-4 text-blue-600" />
                              ) : (
                                <UserCircle2 className="h-4 w-4 text-green-600" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium truncate flex items-center gap-2 cursor-pointer hover:text-blue-600" onClick={() => setExpandedClientId(expandedClientId === client.id ? null : client.id)}>
                                {client.fullName}
                                {docStatus && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        {docStatus.status === 'expired' ? (
                                          <AlertCircle className="h-3 w-3 text-red-500" />
                                        ) : (
                                          <AlertTriangle className="h-3 w-3 text-orange-500" />
                                        )}
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {docStatus.message}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                              <div className="flex gap-1 mt-0.5">
                                <Badge 
                                  variant="outline" 
                                  className={`text-[10px] px-1 py-0 ${
                                    client.managementType === 'agenzia' 
                                      ? 'border-orange-300 text-orange-700 bg-orange-50' 
                                      : 'border-teal-300 text-teal-700 bg-teal-50'
                                  }`}
                                >
                                  {client.managementType === 'agenzia' ? 'AG' : 'ST'}
                                </Badge>
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {client.clientType === 'Ditta individuale' ? 'DI' : client.clientType?.charAt(0)}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs space-y-0.5">
                            <div className="font-mono text-slate-700">{client.taxCode || '—'}</div>
                            {client.vatNumber && (
                              <div className="font-mono text-slate-500">P.I. {client.vatNumber}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs space-y-0.5">
                            {client.phone && (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Phone className="h-3 w-3" />
                                {formatPhoneNumber(client.phone)}
                              </div>
                            )}
                            {client.email && (
                              <div className="flex items-center gap-1 text-slate-600 truncate max-w-[150px]">
                                <Mail className="h-3 w-3" />
                                {client.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-slate-600 truncate max-w-[200px]">
                            {formatAddress(client)}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openClient360(client); }}
                            className="group/badge inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50/60 px-2.5 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
                            data-testid={`client-360-row-${client.id}`}
                            title="Apri Vista Cliente 360° (timeline aggregata di tutte le pratiche)"
                          >
                            <Sparkles className="h-3 w-3 text-indigo-500 opacity-70 group-hover/badge:opacity-100" />
                            <span className="font-semibold text-blue-700">{caseCounts.active}</span>
                            <span className="text-slate-400">/</span>
                            <span className="text-slate-600">{caseCounts.total}</span>
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {onOpenRegistroAttivita && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); onOpenRegistroAttivita(client); }}
                                className="h-7 w-7 p-0 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                                data-testid={`client-registro-${client.id}`}
                                title="Registro Attività del cliente"
                              >
                                <FileBarChart className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClient(client)}
                              className="h-7 w-7 p-0"
                              data-testid={`edit-client-${client.id}`}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClient(client.id)}
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedClientId === client.id && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-slate-50/50 py-2 px-4">
                            <BillingPlanSection
                              clientId={client.id}
                              clientName={client.fullName}
                              managementType={client.managementType || "agenzia"}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              {clientsPaginationBar}
              {filteredClients.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  Nessun cliente trovato
                </div>
              )}
            </div>
          ) : (
            /* Card View */
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredClients.map((client) => {
                const caseCounts = getClientCases(client.id);
                const docStatus = getDocumentExpiryStatus(client);

                return (
                  <Card
                    key={client.id}
                    className={`hover:shadow-lg transition-shadow border-l-4 ${
                      docStatus?.status === 'expired' 
                        ? 'border-l-red-400' 
                        : docStatus?.status === 'warning'
                        ? 'border-l-orange-400'
                        : 'border-l-blue-400'
                    }`}
                    data-testid={`client-card-${client.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                            {client.clientType === "Azienda" || client.clientType === "Ditta individuale" ? (
                              <Building2 className="h-6 w-6 text-blue-600" />
                            ) : (
                              <UserCircle2 className="h-6 w-6 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-base flex items-center gap-2">
                              {client.fullName}
                              {docStatus && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      {docStatus.status === 'expired' ? (
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                      ) : (
                                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                                      )}
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="flex items-center gap-1">
                                        <CreditCard className="h-3 w-3" />
                                        {docStatus.message}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </h3>
                            <div className="flex gap-2 mt-1">
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  client.managementType === 'agenzia' 
                                    ? 'border-orange-300 text-orange-700 bg-orange-50' 
                                    : 'border-teal-300 text-teal-700 bg-teal-50'
                                }`}
                              >
                                {client.managementType === 'agenzia' ? 'Agenzia' : 'Studio'}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {client.clientType}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Separator className="my-3" />

                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                          <span className="text-slate-600 break-all font-mono text-xs">
                            {client.taxCode || "—"}
                          </span>
                        </div>
                        {client.email && (
                          <div className="flex items-start gap-2">
                            <Mail className="h-4 w-4 text-slate-400 mt-0.5" />
                            <span className="text-slate-600 break-all text-xs">
                              {client.email}
                            </span>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-600 text-xs">{formatPhoneNumber(client.phone)}</span>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                          <span className="text-slate-600 text-xs">
                            {formatAddress(client)}
                          </span>
                        </div>
                      </div>

                      {client.notes && (
                        <>
                          <Separator className="my-3" />
                          <div className="flex items-start gap-2">
                            <MessageSquare className="h-4 w-4 text-slate-400 mt-0.5" />
                            <p className="text-xs text-slate-600 italic line-clamp-2">
                              {client.notes}
                            </p>
                          </div>
                        </>
                      )}

                      <Separator className="my-3" />

                      <div className="flex items-center justify-between text-sm">
                        <button
                          type="button"
                          onClick={() => openClient360(client)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50/60 px-2.5 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
                          data-testid={`client-360-card-${client.id}`}
                          title="Apri Vista Cliente 360° (timeline aggregata di tutte le pratiche)"
                        >
                          <Sparkles className="h-3 w-3 text-indigo-500" />
                          <span className="text-slate-600">Pratiche:</span>
                          <span className="font-semibold text-blue-700">{caseCounts.active}</span>
                          <span className="text-slate-400">/ {caseCounts.total}</span>
                        </button>
                        <div className="flex gap-1">
                          {onOpenRegistroAttivita && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenRegistroAttivita(client)}
                              className="h-8 text-violet-600 hover:text-violet-700 hover:border-violet-200"
                              data-testid={`client-registro-card-${client.id}`}
                              title="Registro Attività del cliente"
                            >
                              <FileBarChart className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditClient(client)}
                            className="h-8"
                            data-testid={`edit-client-card-${client.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClient(client.id)}
                            className="h-8 text-red-600 hover:text-red-700 hover:border-red-200"
                            data-testid={`delete-client-card-${client.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {viewMode === 'card' && clientsPaginationBar}

          {filteredClients.length === 0 && viewMode === 'card' && (
            <div className="text-center py-12 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun cliente trovato</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={resetFilters} className="mt-2">
                  Rimuovi filtri
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Document Expiry Alert Popup */}
      <AlertDialog open={showAlertPopup} onOpenChange={setShowAlertPopup}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Bell className="h-5 w-5" />
              Attenzione: Documenti Scaduti!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-slate-700">
                  Sono stati rilevati documenti di identità scaduti o in scadenza che richiedono attenzione immediata:
                </p>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {expiryAlerts.filter(a => a.status === 'expired').map((alert, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <span className="font-medium text-red-800">{alert.client.fullName}</span>
                      </div>
                      <span className="text-sm text-red-600">{alert.message}</span>
                    </div>
                  ))}
                  {expiryAlerts.filter(a => a.status === 'warning').slice(0, 3).map((alert, idx) => (
                    <div 
                      key={`warn-${idx}`}
                      className="flex items-center justify-between p-2 bg-orange-50 border border-orange-200 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                        <span className="font-medium text-orange-800">{alert.client.fullName}</span>
                      </div>
                      <span className="text-sm text-orange-600">{alert.message}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-500">
                  Clicca su un cliente nel banner per aggiornare i dati del documento.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Chiudi</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (expiryAlerts.length > 0 && expiryAlerts[0].client) {
                  handleEditClient(expiryAlerts[0].client);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Gestisci Primo Cliente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cliente 360° — quick-access dalla lista Clienti (17/07/2026 · v1.4.1).
          Non richiede l'apertura della scheda Modifica: apre direttamente
          la timeline aggregata di tutte le pratiche del cliente. */}
      {client360Client && (
        <Client360Dialog
          isOpen={!!client360Client}
          onOpenChange={(open) => { if (!open) closeClient360(); }}
          clientId={client360Client.id}
          clientName={client360Client.fullName}
        />
      )}

      {/* Sweep Anagrafica CF↔Comune (17/07/2026 · v1.4.1 sera).
          Report di qualità dati sull'intera anagrafica: elenca i clienti
          con Comune di Nascita non coerente col codice catastale del CF
          e permette la correzione automatica in un click. */}
      <AnagraficaConsistencyDialog
        isOpen={consistencyOpen}
        onOpenChange={setConsistencyOpen}
        clients={clients}
        onEditClient={handleEditClient}
        onReloadClients={onReloadClients}
      />
    </div>
  );
}

export default React.memo(ClientiTab);
